import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';

interface Props { isOpen: boolean; onClose: () => void; onRegistered?: (email: string) => void; onOpenSignIn: () => void; }

export default function RegisterModal({ isOpen, onClose, onRegistered, onOpenSignIn }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [favoriteStyle, setFavoriteStyle] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const supabaseConfigured = Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);

  if (!isOpen) return null;

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!supabaseConfigured) throw new Error('Supabase is not configured');
      const fullName = `${firstName} ${lastName}`.trim();
      const emailRedirectTo = `${window.location.origin}/auth/confirm?type=signup&next=/`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            first_name: firstName,
            last_name: lastName,
            full_name: fullName || undefined,
            phone,
            favorite_style: favoriteStyle || undefined,
            marketing_opt_in: marketingOptIn || undefined,
            marketing_opt_in_at: marketingOptIn ? Date.now() : undefined,
          },
        },
      });
      if (data && !error) {
        console.log('[auth] Registration successful, email should be sent');
      }
      if (error) throw error;
      onClose();
      try { if (onRegistered) onRegistered(email); } catch {}
    } catch (err: any) {
      const message = (err as Error)?.message;
      try { console.error('[auth] registration failed', err); } catch {}
      setError(`${t('auth.errorRegister')}${message ? `: ${message}` : ''}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex min-h-screen items-center justify-center bg-black/70 backdrop-blur-md p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="register-modal-title">
      <div className="w-full max-w-sm sm:max-w-md bg-gray-900 rounded-lg sm:rounded-xl shadow-2xl border border-pink-600/20 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-800">
          <h3 id="register-modal-title" className="text-lg font-semibold text-white">{t('auth.register')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white px-2 py-1 rounded">✕</button>
        </div>
        <div className="p-5 sm:p-6 space-y-4">
          <form onSubmit={handleRegister} className="space-y-3">
            <label htmlFor="register-email" className="block text-sm text-gray-300">{t('auth.email')}</label>
            <input id="register-email" name="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            <label htmlFor="register-password" className="block text-sm text-gray-300">{t('auth.password')}</label>
            <input id="register-password" name="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="register-first-name" className="block text-sm text-gray-300">{t('auth.firstName')}</label>
                <input id="register-first-name" name="given-name" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
              <div>
                <label htmlFor="register-last-name" className="block text-sm text-gray-300">{t('auth.lastName')}</label>
                <input id="register-last-name" name="family-name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
              </div>
            </div>
            <label htmlFor="register-phone" className="block text-sm text-gray-300">{t('auth.phone')}</label>
            <input id="register-phone" name="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            <label htmlFor="register-favorite-style" className="block text-sm text-gray-300">{t('auth.favoriteStyle')}</label>
            <select id="register-favorite-style" value={favoriteStyle} onChange={(e) => setFavoriteStyle(e.target.value)} required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white">
              <option value="" disabled>{t('auth.favoriteStyle')}</option>
              <option value="fineLine">{t('services.items.fineLine.title')}</option>
              <option value="realistic">{t('services.items.realistic.title')}</option>
              <option value="blackGray">{t('services.items.blackGray.title')}</option>
              <option value="color">{t('services.items.color.title')}</option>
              <option value="coverUps">{t('services.items.coverUps.title')}</option>
              <option value="custom">{t('services.items.custom.title')}</option>
            </select>
            <label className="flex items-start gap-2 text-xs text-gray-400">
              <input type="checkbox" className="mt-0.5 h-4 w-4" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} />
              <span>{t('auth.marketingOptIn', 'I agree to receive promotions by email/SMS. I can unsubscribe anytime.')}</span>
            </label>
            <button disabled={submitting} className="w-full border border-pink-600/40 text-pink-300 hover:text-white hover:border-pink-500 rounded-md py-2">{t('auth.register')}</button>
          </form>
          {!supabaseConfigured && (<p className="text-xs text-yellow-400">{t('auth.supabaseMissing')}</p>)}
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="text-xs text-gray-400">
            {t('auth.haveAccount')} {" "}
            <button type="button" onClick={() => { onClose(); onOpenSignIn(); }} className="underline text-pink-300 hover:text-pink-200">{t('auth.signIn')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


